import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getCurrentUser, loginUser, logoutUser, registerUser, updateUser } from "../services/storage";
import { getToken, onSessionEnd } from "../services/api";

const AuthContext = createContext(null);

// The signed-in host. `ready` turns true once a saved session has been
// checked with the API, so pages don't flash "logged out" on reload.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(() => !getToken());

  useEffect(() => {
    if (ready) return;
    getCurrentUser().then((current) => {
      setUser(current);
      setReady(true);
    });
  }, [ready]);

  // A session that ends on its own (expired, or signed out from another
  // device) sends the host back to the login page.
  useEffect(() => onSessionEnd(() => setUser(null)), []);

  const login = useCallback(async (credentials) => setUser(await loginUser(credentials)), []);
  const register = useCallback(async (details) => setUser(await registerUser(details)), []);
  const logout = useCallback(async () => {
    setUser(null);
    await logoutUser();
  }, []);
  const updateProfile = useCallback(async (changes) => setUser(await updateUser(changes)), []);
  // Reads the account again, e.g. after the email address was confirmed.
  const reloadUser = useCallback(async () => {
    const current = await getCurrentUser();
    if (current) setUser(current);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, logout, updateProfile, reloadUser }),
    [user, ready, login, register, logout, updateProfile, reloadUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
